import React, { useState, useEffect, useContext } from 'react';
import { TranslatorContext } from '../../context/Translator';
import { useAuth } from '../../context/AuthProvider';
import { useBranch } from '../../context/BranchContext';
import { Modal, Form, Tabs, Tab } from 'react-bootstrap';
import { ButtonComponent } from '../elements';
import { userAPI, employeeAPI, attendanceAPI } from '../../services';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import CompetitionAnalysisModal from '../modals/CompetitionAnalysisModal';
import ManualPointModal from '../modals/ManualPointModal';
import EmployeePointHistoryTab from './EmployeePointHistoryTab';
import EmployeeRewardPointHistoryTab from './EmployeeRewardPointHistoryTab';
import EmployeeShiftPreferenceTab from './EmployeeShiftPreferenceTab';
import './users-table.css';

/**
 * #155950 — nhãn "dd/MM/yyyy" theo LỊCH VN (không theo múi giờ của máy đang mở trình duyệt);
 * ngày hỏng thì trả nguyên chuỗi thay vì làm vỡ cả form.
 */
function formatWeekLabelSafe(value) {
  const ymd = toVnYmd(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return String(value);
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * #155950 — quy "ngày bắt đầu + số tuần" ra khoảng hiệu lực THEO TUẦN để quản lý xem trước
 * khi lưu. Tính trên CHUỖI NGÀY (UTC) chứ không dùng giờ máy: ô <input type="date"> đã cho
 * sẵn 'YYYY-MM-DD', đưa qua giờ địa phương chỉ tạo cơ hội lệch 1 ngày.
 *
 * Phải khớp ĐÚNG shift-cap.util.ts phía server (tuần Thứ 2 → Chủ nhật, số tuần tính CẢ tuần
 * chứa ngày bắt đầu) — lệch là quản lý thấy một đằng, hệ thống chặn một nẻo.
 */
function previewCapWindow(fromYmd, weeks) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(fromYmd || ''))) return null;
  const n = Number(weeks);
  if (!Number.isInteger(n) || n < 1) return null;

  const [y, m, d] = fromYmd.split('-').map(Number);
  const base = new Date(Date.UTC(y, m - 1, d));
  const dow = base.getUTCDay(); // 0 = Chủ nhật
  const monday = new Date(base.getTime() - (dow === 0 ? 6 : dow - 1) * 86400000);
  const lastSunday = new Date(monday.getTime() + (n * 7 - 1) * 86400000);
  const fmt = (dt) =>
    `${String(dt.getUTCDate()).padStart(2, '0')}/${String(dt.getUTCMonth() + 1).padStart(2, '0')}/${dt.getUTCFullYear()}`;
  return { start: fmt(monday), end: fmt(lastSunday), weeks: n };
}

/**
 * #155950 — instant ISO từ server → NGÀY LỊCH VN dạng 'YYYY-MM-DD' cho ô <input type="date">.
 *
 * KHÔNG được cắt chuỗi ISO bằng slice(0, 10): server lưu 00:00 giờ VN = 17:00 UTC NGÀY HÔM
 * TRƯỚC, nên cắt thô làm ngày lùi đúng 1 hôm (quản lý nhập 10/09, mở lại thấy 09/09). Cộng
 * +7h rồi đọc phần UTC thì ra đúng ngày VN bất kể máy đang ở múi giờ nào.
 */
function toVnYmd(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  const vn = new Date(d.getTime() + 7 * 3600000);
  return `${vn.getUTCFullYear()}-${String(vn.getUTCMonth() + 1).padStart(2, '0')}-${String(vn.getUTCDate()).padStart(2, '0')}`;
}

/**
 * #155950 — suy ngược ô "Số tuần" từ dữ liệu đã lưu (server chỉ lưu tuần CUỐI còn hiệu lực).
 * Mở lại form phải thấy đúng con số quản lý đã nhập, không bắt họ tự nhớ.
 */
function capWeeksFromRecord(user) {
  if (!user?.shift_cap_from || !user?.shift_cap_until_week) return '';
  const toUtcDay = (v) => {
    const [y, m, d] = toVnYmd(v).split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  const fromDay = toUtcDay(user.shift_cap_from);
  const untilDay = toUtcDay(user.shift_cap_until_week);
  const fromDow = new Date(fromDay).getUTCDay();
  const fromMonday = fromDay - (fromDow === 0 ? 6 : fromDow - 1) * 86400000;
  const weeks = Math.round((untilDay - fromMonday) / (7 * 86400000)) + 1;
  return weeks >= 1 ? String(weeks) : '';
}

/**
 * #155950 — trạng thái trần đã lưu, xét theo TUẦN hiện tại:
 *   'active'   đang chặn duyệt ca ngay tuần này
 *   'upcoming' đã đặt nhưng tuần bắt đầu còn ở phía trước
 *   null       chưa đặt, hoặc đã quá hạn (trần tự hết hiệu lực — không hiện badge nữa)
 * Phân biệt 2 trạng thái đầu để badge không nói "đang bị trần" khi thực tế tuần này NV vẫn
 * được duyệt ca bình thường.
 */
function capStatus(user) {
  if (user?.shift_cap_per_week === null || user?.shift_cap_per_week === undefined) {
    return null;
  }
  if (!user?.shift_cap_until_week) return null;
  const toUtcDay = (v) => {
    const [y, m, d] = toVnYmd(v).split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  const mondayOf = (dayMs) => {
    const dow = new Date(dayMs).getUTCDay();
    return dayMs - (dow === 0 ? 6 : dow - 1) * 86400000;
  };
  const now = new Date();
  const thisMonday = mondayOf(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()),
  );
  if (thisMonday > toUtcDay(user.shift_cap_until_week)) return null;
  if (user.shift_cap_from && thisMonday < mondayOf(toUtcDay(user.shift_cap_from))) {
    return 'upcoming';
  }
  return 'active';
}

export default function UsersTableComponent({ thead, tbody, onRefresh }) {
  const { t } = useContext(TranslatorContext);
  const { user: currentUser } = useAuth();
  const { branches } = useBranch();

  const [userData, setUserData] = React.useState('');
  const [editModal, setEditModal] = React.useState(false);
  const [viewModal, setViewModal] = React.useState(false);
  const [editForm, setEditForm] = useState({
    email: '',
    phone: '',
    role: '',
    branchId: '',
    revenue_reward_rate: 0,
    // #155950 — trần ca/tuần (chuỗi rỗng = không giới hạn) + hạn áp dụng
    shift_cap_per_week: '',
    shift_cap_from: '',
    shift_cap_weeks: '',
    game_manager: false,
    game_manager_branch_ids: [],
    task_creator: false,
    trainer: false,
    flagged: false,
    flag_reason: '',
  });
  const [togglingStatus, setTogglingStatus] = useState({});
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [approvingUser, setApprovingUser] = useState(null);
  const [markingLeaving, setMarkingLeaving] = useState({});
  const [showManualPointModal, setShowManualPointModal] = useState(false);
  const [selectedUserForPoint, setSelectedUserForPoint] = useState(null);

  // #024038 — từ chối hồ sơ ứng tuyển (bắt buộc ghi lý do) + gửi lại mật mã qua Zalo.
  const [rejectingUser, setRejectingUser] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [resendingPasscode, setResendingPasscode] = useState({});
  // #102134 — hồ sơ đã duyệt xong nhưng mật mã CHƯA tới tay nhân viên; giữ trên màn hình cho
  // tới khi người duyệt gửi lại được hoặc tự tắt, vì nó là việc còn dở chứ không phải thông báo.
  const [approveWarning, setApproveWarning] = useState(null);

  // #210314 — xử lý dứt điểm sự cố "lỗi đăng ký khuôn mặt" ngay tại hồ sơ nhân viên.
  const [faceBusy, setFaceBusy] = useState(false);
  const [faceExemptReason, setFaceExemptReason] = useState('');

  useEffect(() => {
    // sync if needed in future
  }, [tbody]);

  // checkbox logic removed; not used in current table

  const handleApprove = async (user) => {
    const branchId = user.branch_id || user.branch?.id;
    if (!branchId) {
      toast.warning('Nhân viên chưa được gán chi nhánh. Vui lòng cập nhật chi nhánh trước khi duyệt.');
      return;
    }

    setApprovingUser(user);
    setShowAnalysisModal(true);
  };

  const handleConfirmApprove = async () => {
    if (!approvingUser) return;
    try {
      const res = await userAPI.approveUser(approvingUser.id);
      const name = approvingUser.full_name || approvingUser.username;

      /**
       * #102134 — DUYỆT và GỬI MẬT MÃ là HAI việc, phải báo tách bạch.
       *
       * Trước đây chỗ này chỉ hiện "Đã duyệt nhân viên thành công" màu xanh cho mọi kết quả,
       * nên khi Zalo hỏng (kênh cá nhân đang chập chờn — điều tra #090433) người duyệt vẫn
       * tưởng nhân viên đã nhận được mật mã. Đúng cái sếp hỏi: *"tôi vào bấm kích hoạt nhưng
       * k biết nhân viên có nhận được tin nhắn mật mã hay không?"*.
       */
      if (res?.already_active) {
        toast.info(`"${name}" đã được duyệt trước đó — không sinh mật mã mới.`);
      } else if (res?.delivery_error) {
        // Cảnh báo DÍNH lại (không tự tắt) vì nó đòi người duyệt phải làm tiếp một việc.
        setApproveWarning({
          user: approvingUser,
          message: res.delivery_error,
        });
        toast.warning(`Đã duyệt "${name}" NHƯNG chưa gửi được mật mã — xem cảnh báo bên dưới.`);
      } else if (res?.passcode_sent) {
        toast.success(`Đã duyệt "${name}" và gửi mật mã đăng nhập qua Zalo.`);
      } else {
        toast.success(`Đã duyệt "${name}".`);
      }

      if (onRefresh) onRefresh();
      setShowAnalysisModal(false);
      setApprovingUser(null);
    } catch (e) {
      console.error('Approve user failed', e);
      toast.error(e?.message || 'Duyệt nhân viên thất bại');
    }
  };

  /** #024038 — mở ô nhập lý do từ chối. */
  const handleOpenReject = (user) => {
    setRejectingUser(user);
    setRejectReason('');
  };

  const handleConfirmReject = async () => {
    if (!rejectingUser) return;
    const reason = rejectReason.trim();
    if (reason.length < 5) {
      toast.warning('Vui lòng ghi lý do từ chối (ít nhất 5 ký tự) — lý do được gửi cho ứng viên.');
      return;
    }
    try {
      setRejecting(true);
      await userAPI.rejectUser(rejectingUser.id, reason);
      toast.success('Đã từ chối hồ sơ và báo ứng viên qua Zalo');
      setRejectingUser(null);
      setRejectReason('');
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Reject user failed', e);
      toast.error(e?.message || 'Từ chối hồ sơ thất bại');
    } finally {
      setRejecting(false);
    }
  };

  /** #024038 — gửi lại mật mã đăng nhập lần đầu; mật mã cũ mất hiệu lực ngay. */
  const handleResendPasscode = async (user) => {
    if (
      !window.confirm(
        `Gửi lại mật mã đăng nhập cho "${user.full_name || user.username}" qua Zalo? Mật mã cũ sẽ hết hiệu lực.`,
      )
    )
      return;
    try {
      setResendingPasscode((prev) => ({ ...prev, [user.id]: true }));
      await userAPI.resendPasscode(user.id);
      toast.success('Đã gửi lại mật mã qua Zalo');
      // #102134 — gửi được rồi thì gỡ cảnh báo "chưa gửi được" của đúng người này.
      setApproveWarning((prev) => (prev && prev.user?.id === user.id ? null : prev));
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Resend passcode failed', e);
      toast.error(e?.message || 'Gửi lại mật mã thất bại');
    } finally {
      setResendingPasscode((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const handleToggleStatus = async (user) => {
    const userId = user.id;
    const currentStatus = (user.status || '').toUpperCase();
    const isActive = currentStatus === 'ACTIVE' || currentStatus === 'APPROVED';
    const newStatus = isActive ? 'INACTIVE' : 'ACTIVE';

    try {
      setTogglingStatus((prev) => ({ ...prev, [userId]: true }));
      await employeeAPI.updateEmployeeStatus(userId, newStatus);
      toast.success(`Đã ${newStatus === 'ACTIVE' ? 'kích hoạt' : 'vô hiệu hóa'} tài khoản`);
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Toggle user status failed', e);
      toast.error('Thay đổi trạng thái thất bại');
    } finally {
      setTogglingStatus((prev) => ({ ...prev, [userId]: false }));
    }
  };

  /**
   * #210314 — Đặt lại khuôn mặt: xoá dữ liệu khuôn mặt để nhân viên đăng ký lại từ đầu.
   * Máy chủ đồng thời đóng sự cố đang mở và gửi tin xác nhận về nhóm "Báo cáo sự cố".
   */
  const handleResetFace = async () => {
    if (!userData?.id) return;
    if (
      !window.confirm(
        `Đặt lại khuôn mặt cho "${userData.full_name || userData.username}"? Nhân viên sẽ phải đăng ký lại từ đầu.`,
      )
    )
      return;
    try {
      setFaceBusy(true);
      await attendanceAPI.resetFaceEnrollment(userData.id);
      toast.success('Đã đặt lại khuôn mặt. Nhân viên có thể đăng ký lại.');
      setUserData((prev) => ({ ...prev, has_face_enrolled: false }));
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Reset face enrollment failed', e);
      toast.error('Đặt lại khuôn mặt thất bại');
    } finally {
      setFaceBusy(false);
    }
  };

  /** #210314 — Miễn / gỡ miễn đăng ký khuôn mặt (bắt buộc ghi lý do khi miễn). */
  const handleSetFaceExemption = async (exempt) => {
    if (!userData?.id) return;
    if (exempt && !faceExemptReason.trim()) {
      toast.warning('Vui lòng ghi lý do miễn đăng ký khuôn mặt.');
      return;
    }
    try {
      setFaceBusy(true);
      await attendanceAPI.setFaceEnrollExemption(
        userData.id,
        exempt,
        faceExemptReason.trim(),
      );
      toast.success(
        exempt
          ? 'Đã miễn đăng ký khuôn mặt cho nhân viên này.'
          : 'Đã bỏ miễn đăng ký khuôn mặt.',
      );
      setUserData((prev) => ({
        ...prev,
        face_enroll_exempt: exempt,
        face_enroll_exempt_reason: exempt ? faceExemptReason.trim() : null,
        face_enroll_exempt_at: exempt ? new Date().toISOString() : null,
      }));
      setFaceExemptReason('');
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Set face exemption failed', e);
      toast.error('Cập nhật miễn đăng ký khuôn mặt thất bại');
    } finally {
      setFaceBusy(false);
    }
  };

  const handleOpenEdit = (user) => {
    setUserData(user);
    const perms = user.permissions || {};
    setEditForm({
      email: user.email || '',
      phone: user.phone_number || user.phone || '',
      role: user.role || '',
      branchId: user.branch_id || user.branch?.id || '',
      revenue_reward_rate: user.revenue_reward_rate || 0,
      shift_cap_per_week:
        user.shift_cap_per_week === null || user.shift_cap_per_week === undefined
          ? ''
          : String(user.shift_cap_per_week),
      // Hiện lại đúng NGÀY quản lý đã chọn; số tuần suy ngược từ tuần cuối còn hiệu lực.
      shift_cap_from: toVnYmd(user.shift_cap_from),
      shift_cap_weeks: capWeeksFromRecord(user),
      game_manager: Boolean(perms.game_manager),
      game_manager_branch_ids: Array.isArray(perms.game_manager_branches)
        ? [...perms.game_manager_branches]
        : [],
      menu_contributor: Boolean(perms.menu_contributor),
      menu_contributor_branch_ids: Array.isArray(perms.menu_contributor_branches)
        ? [...perms.menu_contributor_branches]
        : [],
      task_creator: Boolean(perms.task_creator),
      trainer: Boolean(perms.trainer),
      flagged: Boolean(perms.flagged),
      flag_reason: perms.flag_reason || '',
    });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const id = userData.id;
      const patch = {
        email: editForm.email,
        phone_number: editForm.phone,
        branch_id: editForm.branchId || null,
        revenue_reward_rate: Number(editForm.revenue_reward_rate),
        // #155950 — để trống = gỡ trần (gửi null tường minh, không bỏ field, nếu không
        // server sẽ giữ nguyên trần cũ và quản lý tưởng đã gỡ).
        ...(String(editForm.shift_cap_per_week).trim() === ''
          ? { shift_cap_per_week: null }
          : {
              shift_cap_per_week: Number(editForm.shift_cap_per_week),
              shift_cap_from: editForm.shift_cap_from,
              shift_cap_weeks: Number(editForm.shift_cap_weeks),
            }),
      };
      if (currentUser?.role === 'ADMIN') {
        patch.permissions = {
          game_manager: Boolean(editForm.game_manager),
          game_manager_branches: editForm.game_manager && editForm.game_manager_branch_ids.length
            ? editForm.game_manager_branch_ids
            : [],
          menu_contributor: Boolean(editForm.menu_contributor),
          menu_contributor_branches: editForm.menu_contributor && editForm.menu_contributor_branch_ids.length
            ? editForm.menu_contributor_branch_ids
            : [],
          task_creator: Boolean(editForm.task_creator),
          trainer: Boolean(editForm.trainer),
          flagged: Boolean(editForm.flagged),
          flag_reason: editForm.flagged ? (editForm.flag_reason || '') : '',
        };
      }
      // Update info via PATCH /employees/:id
      await employeeAPI.updateEmployee(id, patch);
      // Update role if changed
      if (editForm.role && editForm.role !== userData.role) {
        await userAPI.updateUserRole(id, editForm.role);
      }
      toast.success('Cập nhật thông tin nhân viên thành công');
      setEditModal(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Update user failed', e);
      toast.error(e?.message || 'Không thể cập nhật thông tin nhân viên');
    }
  };

  const handleResetRevenueRewardRate = async () => {
    if (!userData?.id) return;
    if (
      !window.confirm(
        `Bạn có chắc muốn reset thưởng tích luỹ về 0 cho nhân viên "${userData.full_name || userData.username}"?`,
      )
    ) {
      return;
    }
    try {
      await employeeAPI.updateEmployee(userData.id, {
        revenue_reward_rate: 0,
        reward_rate_reset: true,
      });
      toast.success('Đã reset thưởng tích luỹ về 0 thành công');
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Reset revenue reward rate failed', e);
      toast.error('Không thể reset thưởng tích luỹ');
    }
  };

  const handleResetRevenueRewardRateForRow = async (user) => {
    if (!user?.id) return;
    if (
      !window.confirm(
        `Bạn có chắc muốn reset thưởng tích luỹ về 0 cho nhân viên "${user.full_name || user.username}"?`,
      )
    ) {
      return;
    }
    try {
      await employeeAPI.updateEmployee(user.id, {
        revenue_reward_rate: 0,
        reward_rate_reset: true,
      });
      toast.success('Đã reset thưởng tích luỹ về 0 thành công');
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Reset revenue reward rate failed', e);
      toast.error('Không thể reset thưởng tích luỹ');
    }
  };

  const handleMarkLeaving = async (user) => {
    if (!window.confirm(`Đánh dấu nhân viên "${user.full_name || user.username}" đã rời đi?`)) return;
    const userId = user.id;
    try {
      setMarkingLeaving((prev) => ({ ...prev, [userId]: true }));
      await employeeAPI.markAsLeaving(userId);
      toast.success('Đã đánh dấu nhân viên rời đi và vô hiệu hóa ca ưu tiên');
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Mark leaving failed', e);
      toast.error('Không thể đánh dấu nhân viên rời đi');
    } finally {
      setMarkingLeaving((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // Đổi mật khẩu bị giới hạn theo backend (chỉ tự đổi). Ẩn khỏi bảng quản trị.

  return (
    <div className="mc-table-responsive">
      {/* #102134 — "đã duyệt NHƯNG mật mã chưa tới tay nhân viên" là một VIỆC CÒN DỞ, nên nó
          nằm lại trên màn hình kèm đúng nút để làm nốt, thay vì một toast trôi qua 3 giây. */}
      {approveWarning && (
        <div
          className="alert alert-warning d-flex flex-wrap align-items-center gap-2 mb-3"
          role="alert"
        >
          <span className="material-icons">warning</span>
          <div className="flex-grow-1">
            <strong>
              Đã duyệt {approveWarning.user.full_name || approveWarning.user.username} — nhưng
              CHƯA gửi được mật mã đăng nhập.
            </strong>
            <div className="small">
              {approveWarning.message} Nhân viên chưa đăng nhập được cho tới khi nhận được mật mã.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-warning"
            disabled={resendingPasscode[approveWarning.user.id]}
            onClick={() => handleResendPasscode(approveWarning.user)}
          >
            {resendingPasscode[approveWarning.user.id] ? 'Đang gửi…' : 'Gửi lại mật mã'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setApproveWarning(null)}
          >
            Đóng
          </button>
        </div>
      )}
      <table className="mc-table">
        <thead className="mc-table-head primary">
          <tr>
            {/* <th className="text-center">ID</th> */}
            <th className="text-center">Họ tên</th>
            <th className="text-center">Username</th>
            <th className="text-center">Quyền đặc biệt</th>
            <th className="text-center">Tỉ lệ thưởng tích luỹ</th>
            {/* <th className="text-center">Email</th> */}
            {/* <th className="text-center">Vai trò</th> */}
            {/* <th className="text-center">Chi nhánh</th> */}
            <th className="text-center">Trạng thái</th>
            <th className="text-center">Thao tác</th>
          </tr>
        </thead>

        <tbody className="mc-table-body even">
          {tbody.map((user, index) => (
            <tr key={index}>
              {/* <th className="text-center">{user.id}</th> */}
              <th className="text-center">
                <div className="d-flex align-items-center justify-content-center gap-1">
                  {user.full_name}
                  {user.permissions?.flagged && (
                    <span
                      className="material-icons text-danger"
                      style={{ fontSize: '16px' }}
                      title={user.permissions?.flag_reason ? `Đang bị gắn cờ: ${user.permissions.flag_reason}` : 'Đang bị gắn cờ'}
                    >
                      flag
                    </span>
                  )}
                </div>
              </th>
              <th className="text-center">{user.username}</th>
              <th className="text-center">
                <div className="d-flex gap-1 justify-content-center flex-wrap">
                  {user.permissions?.game_manager && (
                    <span className="badge bg-warning text-dark">GM</span>
                  )}
                  {user.permissions?.menu_contributor && (
                    <span className="badge bg-info text-dark">MC</span>
                  )}
                  {user.permissions?.trainer && (
                    <span className="badge bg-success text-white">TR</span>
                  )}
                  {user.permissions?.flagged && (
                    <span className="badge bg-danger text-white">FLAG</span>
                  )}
                  {!user.permissions?.game_manager &&
                    !user.permissions?.menu_contributor &&
                    !user.permissions?.trainer &&
                    !user.permissions?.flagged && (
                      <span className="text-muted small">—</span>
                    )}
                </div>
              </th>
              <th className="gap-2  d-flex mt-3 justify-content-center justify-content-center">
                {user.revenue_reward_rate || '0%'} <div className="mc-table-action small text-muted">
                  <ButtonComponent
                    title="Reset thưởng tích luỹ"
                    className="material-icons delete"
                    onClick={() => handleResetRevenueRewardRateForRow(user)}
                  >
                    restart_alt
                  </ButtonComponent>
                </div>
              </th>
              {/* <th className="text-center">{user.email || ''}</th> */}
              {/* <th className="text-center">{user.role}</th> */}
              {/* <th className="text-center">{user.branch?.name || user.branch_id || ''}</th> */}
              <td className="text-center">
                {/* #024038 — hồ sơ ứng tuyển (chờ duyệt / bị từ chối) KHÔNG phải tài khoản
                    bật-tắt được, nên hiện nhãn trạng thái thay cho công tắc. */}
                {(user.status || '').toUpperCase() === 'PENDING_REVIEW' ? (
                  <span className="badge bg-warning text-dark">Chờ duyệt</span>
                ) : (user.status || '').toUpperCase() === 'REJECTED' ? (
                  <span
                    className="badge bg-danger text-white"
                    title={user.rejection_reason || 'Đã từ chối'}
                  >
                    Đã từ chối
                  </span>
                ) : (
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <Form.Check
                      type="switch"
                      id={`status-switch-${user.id}`}
                      checked={
                        (user.status || '').toUpperCase() === 'ACTIVE' || (user.status || '').toUpperCase() === 'APPROVED'
                      }
                      onChange={() => handleToggleStatus(user)}
                      disabled={togglingStatus[user.id]}
                    />
                    {togglingStatus[user.id] ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <span className="small text-muted">
                        {(user.status || '').toUpperCase() === 'ACTIVE' ||
                        (user.status || '').toUpperCase() === 'APPROVED'
                          ? 'Hoạt động'
                          : 'Đã tắt'}
                      </span>
                    )}
                  </div>
                )}
                {user.must_change_password && (
                  <div className="small text-muted mt-1" title="Nhân viên chưa đăng nhập đổi mật khẩu lần đầu">
                    Chưa đổi mật mã
                  </div>
                )}
                {/* #090432 — hồ sơ nộp lúc Zalo gửi mã hỏng: SĐT CHƯA qua xác thực, người duyệt
                    phải tự gọi/kiểm số trước khi bấm Duyệt. */}
                {user.phone_verified === false && (
                  <div
                    className="small text-danger mt-1"
                    title={
                      user.phone_verify_skip_reason === 'sidecar_not_configured'
                        ? 'Bỏ qua xác thực: hệ thống Zalo chưa được cấu hình lúc nộp hồ sơ'
                        : 'Bỏ qua xác thực: hệ thống không gửi được mã Zalo lúc nộp hồ sơ'
                    }
                  >
                    SĐT chưa xác thực
                  </div>
                )}
              </td>
              <td>
                <div className="mc-table-action">
                  {/* #112736 — Hồ sơ ứng tuyển (PENDING_REVIEW): nút DUYỆT phải NHÌN THẤY RÕ.
                      Trước đây nút duyệt chỉ là icon kính lúp (`search`) kèm tooltip tiếng Anh
                      "Approve" nên bị lẫn với "xem/tìm kiếm"; trên điện thoại tooltip không hiện,
                      quản lý chỉ thấy nút từ chối ⇒ tưởng không có nút duyệt. Nay hiện CHỮ
                      "Duyệt hồ sơ" + icon người-có-dấu-tích cho hồ sơ chờ duyệt. */}
                  {(user.status || '').toUpperCase() === 'PENDING_REVIEW' ? (
                    <ButtonComponent
                      title="Duyệt hồ sơ ứng tuyển"
                      className="btn-duyet-ho-so"
                      onClick={() => handleApprove(user)}
                    >
                      <i className="material-icons">how_to_reg</i>
                      Duyệt hồ sơ
                    </ButtonComponent>
                  ) : (
                    <ButtonComponent
                      title="Duyệt nhân viên"
                      className="material-icons download"
                      onClick={() => handleApprove(user)}
                    >
                      how_to_reg
                    </ButtonComponent>
                  )}
                  {/* #024038 — Từ chối hồ sơ: chỉ có nghĩa khi hồ sơ chưa được duyệt. */}
                  {(user.status || '').toUpperCase() === 'PENDING_REVIEW' && (
                    <ButtonComponent
                      title="Từ chối hồ sơ"
                      className="material-icons delete"
                      onClick={() => handleOpenReject(user)}
                    >
                      person_off
                    </ButtonComponent>
                  )}
                  {/* #024038 — Gửi lại mật mã: chỉ khi NV chưa đăng nhập đổi mật khẩu lần đầu. */}
                  {user.must_change_password && (
                    <ButtonComponent
                      title="Gửi lại mật mã qua Zalo"
                      className="material-icons edit"
                      onClick={() => handleResendPasscode(user)}
                      disabled={resendingPasscode[user.id]}
                    >
                      {resendingPasscode[user.id] ? 'hourglass_top' : 'forward_to_inbox'}
                    </ButtonComponent>
                  )}
                  <ButtonComponent
                    title="View"
                    className="material-icons view"
                    onClick={() => {
                      setUserData(user);
                      setViewModal(true);
                    }}
                  >
                    visibility
                  </ButtonComponent>
                  <ButtonComponent
                    title="Edit"
                    className="material-icons edit"
                    onClick={() => handleOpenEdit(user)}
                  >
                    edit
                  </ButtonComponent>
                  <ButtonComponent
                    title="Đánh dấu rời đi"
                    className="material-icons block"
                    onClick={() => handleMarkLeaving(user)}
                    disabled={markingLeaving[user.id]}
                  >
                    {markingLeaving[user.id] ? 'hourglass_top' : 'logout'}
                  </ButtonComponent>
                  <ButtonComponent
                    title="Thêm/Trừ điểm"
                    className="material-icons delete"
                    onClick={() => {
                      setSelectedUserForPoint(user);
                      setShowManualPointModal(true);
                    }}
                  >
                    add
                  </ButtonComponent>
                 
                  {/* Change password chỉ cho trang cá nhân */}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        {/* <tbody className="mc-table-body even">
                    {data?.map((item, index) => (
                        <tr key={ index }> 
                            <td title="id">
                                <div className="mc-table-check">
                                    <input 
                                        type="checkbox" 
                                        name={item.name} 
                                        checked={ item?.isChecked || false }
                                        onChange={ handleCheckbox } 
                                    />
                                    <p>#{ index + 1 }</p>
                                </div>
                            </td>
                            <td title={ item.name }>
                                <div className="mc-table-profile">
                                    <img src={ item.src } alt={ item.alt } />
                                    <p>{ item.name }</p>
                                </div>
                            </td>
                            <td title={ item.role }>
                                <div className="mc-table-icon role">
                                    { item.role.text === "vendor" && <i className="material-icons yellow">{ item.role.icon }</i> }
                                    { item.role.text === "member" && <i className="material-icons green">{ item.role.icon }</i> }
                                    { item.role.text === "admin" && <i className="material-icons purple">{ item.role.icon }</i> }
                                    { item.role.text === "founder" && <i className="material-icons blue">{ item.role.icon }</i> }
                                    <span>{ item.role.text }</span>
                                </div>
                            </td>
                            <td title={ item.email }>{ item.email }</td>
                            <td title={ item.password }>{ item.password }</td>
                            <td title={ item.phone }>{ item.phone }</td>
                            <td title={ item.status }>
                                { item.status === "approved" && <p className="mc-table-badge green">{ item.status }</p> }
                                { item.status === "pending" && <p className="mc-table-badge purple">{ item.status }</p> }
                                { item.status === "blocked" && <p className="mc-table-badge red">{ item.status }</p> }
                            </td>
                            <td title={ item.created }>{ item.created }</td>
                            <td>
                                <div className="mc-table-action">
                                    <AnchorComponent to="/user-profile" title="View" className="material-icons view">{ item.action.view }</AnchorComponent>
                                    <ButtonComponent title="Edit" className="material-icons edit" onClick={()=> setEditModal(true, setUserData(item))}>{ item.action.edit }</ButtonComponent>
                                    <ButtonComponent title="Block" className="material-icons block" onClick={()=> setBlockModal(true)}>{ item.action.block }</ButtonComponent>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody> */}
      </table>

      <Modal
        show={viewModal}
        onHide={() => setViewModal(false)}
        size="xl"
        centered
        className="mc-user-view-modal"
      >
        <Modal.Header
          closeButton
          className="border-0 pb-0"
        >
          <Modal.Title className="w-100">
            <div className="d-flex align-items-center gap-3">
              {userData?.avatar_url && (
                <img
                  src={userData.avatar_url}
                  alt="avatar"
                  className="rounded-circle border"
                  style={{ inlineSize: '80px', blockSize: '80px', objectFit: 'cover' }}
                />
              )}
              <div>
                <h4 className="mb-1">{userData?.full_name || userData?.username || 'N/A'}</h4>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="badge bg-primary">{userData?.role || '-'}</span>
                  {userData?.mfa_enabled && <span className="badge bg-success">MFA</span>}
                  {userData?.permissions?.flagged && (
                    <span className="badge bg-danger">
                      Đang bị gắn cờ{userData?.permissions?.flag_reason ? `: ${userData.permissions.flag_reason}` : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Tabs defaultActiveKey="info" className="mb-3">
            <Tab eventKey="info" title="Thông tin">
          <div className="row g-4">
            <div className="col-md-6">
              <div className="card border-0 bg-light h-100">
                <div className="card-body">
                  <h6 className="text-muted mb-3">Thông tin cá nhân</h6>
                  <div className="d-flex flex-column gap-2">
                    <div>
                      <small className="text-muted d-block">Username</small>
                      <div className="fw-medium">{userData?.username || '-'}</div>
                    </div>
                    <div>
                      <small className="text-muted d-block">Email</small>
                      <div>{userData?.email || '-'}</div>
                    </div>
                    <div>
                      <small className="text-muted d-block">Số điện thoại</small>
                      <div>{userData?.phone_number || '-'}</div>
                    </div>
                    <div>
                      <small className="text-muted d-block">Năm sinh</small>
                      <div>{userData?.birth_year || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-0 bg-light h-100">
                <div className="card-body">
                  <h6 className="text-muted mb-3">Thông tin công việc</h6>
                  <div className="d-flex flex-column gap-2">
                    <div>
                      <small className="text-muted d-block">Chi nhánh</small>
                      <div className="fw-medium">{userData?.branch?.name || userData?.branch_id || '-'}</div>
                    </div>
                    <div>
                      <small className="text-muted d-block">Vai trò</small>
                      <div>
                        <span className="badge bg-primary">{userData?.role || '-'}</span>
                      </div>
                    </div>
                    <div>
                      <small className="text-muted d-block">Đăng nhập gần nhất</small>
                      <div className="small">
                        {userData?.last_login ? new Date(userData.last_login).toLocaleString('vi-VN') : '-'}
                      </div>
                    </div>
                    <div>
                      <small className="text-muted d-block">Bảo mật 2 lớp (MFA)</small>
                      <div>
                        {userData?.mfa_enabled ? (
                          <span className="badge bg-success">Đã bật</span>
                        ) : (
                          <span className="badge bg-secondary">Chưa bật</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <small className="text-muted d-block">Telegram (chat ID)</small>
                      <div className="small">{userData?.telegram_id || '—'}</div>
                    </div>
                    <div>
                      <small className="text-muted d-block">Game master</small>
                      <div>
                        {userData?.permissions?.game_manager ? (
                          <span className="badge bg-warning text-dark">Bật</span>
                        ) : (
                          <span className="text-muted">Tắt</span>
                        )}
                      </div>
                    </div>
                    {userData?.permissions?.game_manager &&
                      Array.isArray(userData.permissions.game_manager_branches) &&
                      userData.permissions.game_manager_branches.length > 0 && (
                        <div>
                          <small className="text-muted d-block">Chi nhánh GM</small>
                          <div className="small">
                            {userData.permissions.game_manager_branches
                              .map((bid) => {
                                const b = branches.find((x) => x.id === bid);
                                return b ? `${b.name} (${b.code})` : bid;
                              })
                              .join(', ')}
                          </div>
                        </div>
                      )}
                    <div>
                      <small className="text-muted d-block">Quản lý món</small>
                      <div>
                        {userData?.permissions?.menu_contributor ? (
                          <span className="badge bg-info text-dark">Bật</span>
                        ) : (
                          <span className="text-muted">Tắt</span>
                        )}
                      </div>
                    </div>
                    {userData?.permissions?.menu_contributor &&
                      Array.isArray(userData.permissions.menu_contributor_branches) &&
                      userData.permissions.menu_contributor_branches.length > 0 && (
                        <div>
                          <small className="text-muted d-block">Chi nhánh QL món</small>
                          <div className="small">
                            {userData.permissions.menu_contributor_branches
                              .map((bid) => {
                                const b = branches.find((x) => x.id === bid);
                                return b ? `${b.name} (${b.code})` : bid;
                              })
                              .join(', ')}
                          </div>
                        </div>
                      )}
                    <div>
                      <small className="text-muted d-block">Trainer</small>
                      <div>
                        {userData?.permissions?.trainer ? (
                          <span className="badge bg-success text-white">Bật (toàn hệ thống)</span>
                        ) : (
                          <span className="text-muted">Tắt</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {(userData?.cccd_front_url || userData?.cccd_back_url) && (
              <div className="col-12">
                <div className="card border-0 bg-light">
                  <div className="card-body">
                    <h6 className="text-muted mb-3">Giấy tờ tùy thân</h6>
                    <div className="row g-3">
                      {userData?.cccd_front_url && (
                        <div className="col-md-6">
                          <a
                            href={userData.cccd_front_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-block position-relative"
                          >
                            <img
                              src={userData.cccd_front_url}
                              alt="cccd-front"
                              className="img-fluid rounded border"
                              style={{ aspectRatio: '16/10', objectFit: 'cover' }}
                            />
                            <div className="position-absolute top-0 end-0 m-2">
                              <span className="badge bg-dark bg-opacity-75">
                                <i
                                  className="material-icons"
                                  style={{ fontSize: '16px', verticalAlign: 'middle' }}
                                >
                                  open_in_new
                                </i>
                              </span>
                            </div>
                          </a>
                          <div className="text-muted small mt-2">CCCD Mặt trước</div>
                        </div>
                      )}
                      {userData?.cccd_back_url && (
                        <div className="col-md-6">
                          <a
                            href={userData.cccd_back_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-block position-relative"
                          >
                            <img
                              src={userData.cccd_back_url}
                              alt="cccd-back"
                              className="img-fluid rounded border"
                              style={{ aspectRatio: '16/10', objectFit: 'cover' }}
                            />
                            <div className="position-absolute top-0 end-0 m-2">
                              <span className="badge bg-dark bg-opacity-75">
                                <i
                                  className="material-icons"
                                  style={{ fontSize: '16px', verticalAlign: 'middle' }}
                                >
                                  open_in_new
                                </i>
                              </span>
                            </div>
                          </a>
                          <div className="text-muted small mt-2">CCCD Mặt sau</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
            </Tab>
            {/* #210314 — Chỗ quản lý XỬ LÝ DỨT ĐIỂM sự cố "lỗi đăng ký khuôn mặt": xem trạng
                thái, đặt lại để đăng ký lại, hoặc miễn hẳn. Đây là đường thoát khỏi vòng lặp
                cảnh báo — dùng lại đúng hồ sơ nhân viên mà tin cảnh báo đã trỏ tới, không đẻ
                màn hình mới. */}
            <Tab eventKey="face" title="Khuôn mặt">
              <div className="p-2">
                <div className="mb-3">
                  <small className="text-muted d-block">Trạng thái đăng ký</small>
                  <div>
                    {userData?.face_enroll_exempt ? (
                      <span className="badge bg-warning text-dark">
                        Được miễn đăng ký khuôn mặt
                      </span>
                    ) : userData?.has_face_enrolled ? (
                      <span className="badge bg-success text-white">Đã đăng ký</span>
                    ) : (
                      <span className="badge bg-secondary text-white">Chưa đăng ký</span>
                    )}
                  </div>
                </div>

                {userData?.face_enroll_exempt && (
                  <div className="mb-3">
                    <small className="text-muted d-block">Lý do miễn</small>
                    <div>{userData?.face_enroll_exempt_reason || '—'}</div>
                    {userData?.face_enroll_exempt_at && (
                      <small className="text-muted">
                        Miễn lúc{' '}
                        {format(
                          new Date(userData.face_enroll_exempt_at),
                          'dd/MM/yyyy HH:mm',
                        )}
                      </small>
                    )}
                  </div>
                )}

                <hr />

                <div className="mb-3">
                  <div className="fw-semibold mb-1">Đặt lại khuôn mặt</div>
                  <p className="text-muted small mb-2">
                    Xoá dữ liệu khuôn mặt hiện tại để nhân viên đăng ký lại từ đầu. Dùng khi
                    ảnh cũ sai người hoặc nhân viên đăng ký hộ nhau.
                  </p>
                  <ButtonComponent
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleResetFace}
                    disabled={faceBusy || !userData?.has_face_enrolled}
                  >
                    {faceBusy ? 'Đang xử lý...' : 'Đặt lại khuôn mặt'}
                  </ButtonComponent>
                  {!userData?.has_face_enrolled && (
                    <div className="text-muted small mt-1">
                      Nhân viên chưa có dữ liệu khuôn mặt để đặt lại.
                    </div>
                  )}
                </div>

                <div>
                  <div className="fw-semibold mb-1">Miễn đăng ký khuôn mặt</div>
                  <p className="text-muted small mb-2">
                    Dùng khi máy của nhân viên không sao nhận diện được. Bật xong hệ thống
                    ngừng bắt đăng ký và ngừng gửi cảnh báo về nhóm sự cố cho nhân viên này.
                  </p>
                  {userData?.face_enroll_exempt ? (
                    <ButtonComponent
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => handleSetFaceExemption(false)}
                      disabled={faceBusy}
                    >
                      {faceBusy ? 'Đang xử lý...' : 'Bỏ miễn (bắt đăng ký lại)'}
                    </ButtonComponent>
                  ) : (
                    <>
                      <Form.Control
                        type="text"
                        placeholder="Lý do miễn (bắt buộc)"
                        value={faceExemptReason}
                        onChange={(e) => setFaceExemptReason(e.target.value)}
                        className="mb-2"
                      />
                      <ButtonComponent
                        type="button"
                        className="btn btn-warning"
                        onClick={() => handleSetFaceExemption(true)}
                        disabled={faceBusy}
                      >
                        {faceBusy ? 'Đang xử lý...' : 'Miễn đăng ký khuôn mặt'}
                      </ButtonComponent>
                    </>
                  )}
                </div>
              </div>
            </Tab>
            {/* #221938: 2 lịch sử ĐỨNG CẠNH NHAU và gọi đúng tên — tab cũ là TỈ LỆ thưởng
                doanh thu (%), tab mới là số ĐIỂM thưởng/phạt (bảng Point). */}
            <Tab eventKey="point-history" title="Lịch sử tỷ lệ thưởng">
              <EmployeePointHistoryTab employeeId={userData?.id} />
            </Tab>
            <Tab eventKey="reward-point-history" title="Lịch sử điểm thưởng">
              <EmployeeRewardPointHistoryTab employeeId={userData?.id} />
            </Tab>
            {/* #004452 — chỗ xem ca ưu tiên nhân viên đã đăng ký, ngay trong hồ sơ nhân viên. */}
            <Tab eventKey="shift-preference" title="Ca ưu tiên">
              <EmployeeShiftPreferenceTab employeeId={userData?.id} />
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <ButtonComponent
            type="button"
            className="btn btn-secondary"
            onClick={() => setViewModal(false)}
          >
            {t('close_popup')}
          </ButtonComponent>
        </Modal.Footer>
      </Modal>

      <Modal
        show={editModal}
        onHide={() => setEditModal(false, setUserData(''))}
        size="lg"
        centered
        className="mc-user-edit-modal"
      >
        <div className="mc-user-modal">
          <h4 className="mb-1">{userData?.full_name || userData?.username}</h4>
          <p className="text-muted mb-4">{userData?.email}</p>
          <Form>
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group className="form-group">
                  <Form.Label>{t('role')}</Form.Label>
                  <Form.Select
                    value={editForm.role}
                    onChange={(e) => setEditForm((s) => ({ ...s, role: e.target.value }))}
                  >
                    <option value="ADMIN">ADMIN</option>
                    <option value="MANAGER">MANAGER</option>
                    <option value="STAFF">STAFF</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="form-group">
                  <Form.Label>Chi nhánh</Form.Label>
                  <Form.Select
                    value={editForm.branchId}
                    onChange={(e) => setEditForm((s) => ({ ...s, branchId: e.target.value }))}
                  >
                    <option value="">-- Không gán chi nhánh --</option>
                    {branches.map((branch) => (
                      <option
                        key={branch.id}
                        value={branch.id}
                      >
                        {branch.name} ({branch.code})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="form-group">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="form-group">
                  <Form.Label>{t('phone')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((s) => ({ ...s, phone: e.target.value }))}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="form-group">
                  <Form.Label>Tỉ lệ thưởng tích luỹ (%)</Form.Label>
                  {/* #122342: mọi đường TRỪ tự động đã bị kẹp sàn 0 ở server; ngoại lệ duy
                      nhất là ADMIN chủ động nhập số âm ở ô này (có ghi lịch sử ai/lúc nào/từ
                      bao nhiêu). MANAGER vẫn bị chặn cả ở ô nhập lẫn ở API (403). */}
                  <Form.Control
                    type="number"
                    min={currentUser?.role === 'ADMIN' ? '-100' : '0'}
                    max="100"
                    step="0.01"
                    value={editForm.revenue_reward_rate}
                    onChange={(e) => setEditForm((s) => ({ ...s, revenue_reward_rate: parseFloat(e.target.value) || 0 }))}
                  />
                  <Form.Text className="text-muted">
                    {currentUser?.role === 'ADMIN'
                      ? 'Tỉ lệ phần trăm thưởng tích luỹ (0-100). Chỉ ADMIN được nhập số ÂM — mọi khoản trừ tự động chỉ xuống tới 0.'
                      : 'Tỉ lệ phần trăm thưởng tích luỹ (0-100). Không nhập số âm — chỉ ADMIN mới được đặt tỉ lệ âm.'}
                  </Form.Text>
                </Form.Group>
              </div>
              {/* #155950 — TRẦN CA: số ca tối đa/tuần bot lẫn quản lý được duyệt cho NV này. */}
              <div className="col-md-6">
                <Form.Group className="form-group">
                  <Form.Label>Trần ca/tuần</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="21"
                    step="1"
                    inputMode="numeric"
                    placeholder="Để trống = không giới hạn"
                    /* 16px: dưới mức này Safari iOS tự phóng to trang khi bấm vào ô nhập. */
                    style={{ fontSize: '16px' }}
                    value={editForm.shift_cap_per_week}
                    onChange={(e) =>
                      setEditForm((s) => ({ ...s, shift_cap_per_week: e.target.value }))
                    }
                  />
                  <Form.Text className="text-muted">
                    Số ca TỐI ĐA được duyệt trong 1 tuần (tính cả bot tự duyệt lẫn quản lý duyệt tay).
                    Để trống = không giới hạn. Ca đã duyệt trước đó không bị huỷ khi đặt trần.
                  </Form.Text>
                  {capStatus(userData) && (
                    <div className="mt-2">
                      <span
                        className={`badge text-white ${capStatus(userData) === 'active' ? 'bg-danger' : 'bg-warning'}`}
                        style={{
                          whiteSpace: 'normal',
                          textAlign: 'left',
                          display: 'inline-block',
                          maxWidth: '100%',
                        }}
                      >
                        {capStatus(userData) === 'active'
                          ? `🔻 Trần ${userData.shift_cap_per_week} ca/tuần — hiệu lực tới hết tuần ${formatWeekLabelSafe(userData.shift_cap_until_week)}`
                          : `🕒 Sắp áp trần ${userData.shift_cap_per_week} ca/tuần — từ ${formatWeekLabelSafe(userData.shift_cap_from)} tới hết tuần ${formatWeekLabelSafe(userData.shift_cap_until_week)}`}
                        {userData.shift_cap_note ? ` — ${userData.shift_cap_note}` : ''}
                      </span>
                      <Form.Text className="text-muted d-block">
                        Hết hạn thì trần tự gỡ, không cần ai bấm.
                      </Form.Text>
                    </div>
                  )}
                </Form.Group>
              </div>
              {/* 2 ô thời hạn chỉ có nghĩa khi đã nhập trần — trống trần = không giới hạn. */}
              {String(editForm.shift_cap_per_week).trim() !== '' && (
                <>
                  <div className="col-md-3">
                    <Form.Group className="form-group">
                      <Form.Label>Áp dụng từ ngày</Form.Label>
                      <Form.Control
                        type="date"
                        style={{ fontSize: '16px' }}
                        value={editForm.shift_cap_from}
                        onChange={(e) =>
                          setEditForm((s) => ({ ...s, shift_cap_from: e.target.value }))
                        }
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-3">
                    <Form.Group className="form-group">
                      <Form.Label>Số tuần áp dụng</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="52"
                        step="1"
                        inputMode="numeric"
                        placeholder="VD: 2"
                        style={{ fontSize: '16px' }}
                        value={editForm.shift_cap_weeks}
                        onChange={(e) =>
                          setEditForm((s) => ({ ...s, shift_cap_weeks: e.target.value }))
                        }
                      />
                    </Form.Group>
                  </div>
                  <div className="col-12">
                    {previewCapWindow(editForm.shift_cap_from, editForm.shift_cap_weeks) ? (
                      <div className="alert alert-info py-2 px-3 mb-2" style={{ fontSize: '14px' }}>
                        Có hiệu lực từ <b>Thứ 2 {previewCapWindow(editForm.shift_cap_from, editForm.shift_cap_weeks).start}</b>{' '}
                        tới hết <b>Chủ nhật {previewCapWindow(editForm.shift_cap_from, editForm.shift_cap_weeks).end}</b>{' '}
                        ({previewCapWindow(editForm.shift_cap_from, editForm.shift_cap_weeks).weeks} tuần) — sau đó tự gỡ.
                        <div className="text-muted">
                          Trần tính theo TUẦN (Thứ 2 → Chủ nhật) cho khớp lịch chạy của bot duyệt ca,
                          nên ngày bắt đầu được quy về đầu tuần chứa nó.
                        </div>
                      </div>
                    ) : (
                      <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: '14px' }}>
                        Nhập <b>ngày bắt đầu</b> và <b>số tuần áp dụng</b> để đặt trần (bỏ trống ô "Trần ca/tuần" nếu muốn gỡ trần).
                      </div>
                    )}
                  </div>
                </>
              )}
              {currentUser?.role === 'ADMIN' && (
                <div className="col-12">
                  <hr className="my-2" />
                  <h6 className="text-muted mb-3">Quyền đặc biệt</h6>
                  <Form.Check
                    type="switch"
                    id="edit-game-manager"
                    className="mb-3"
                    label="Game master (nhận cảnh báo game check qua Telegram)"
                    checked={editForm.game_manager}
                    onChange={(e) =>
                      setEditForm((s) => ({ ...s, game_manager: e.target.checked }))
                    }
                  />
                  {editForm.game_manager && (
                    <Form.Group className="form-group mb-3">
                      <Form.Label>Giới hạn chi nhánh GM (để trống = tất cả)</Form.Label>
                      <Form.Select
                        multiple
                        value={editForm.game_manager_branch_ids}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                          setEditForm((s) => ({ ...s, game_manager_branch_ids: selected }));
                        }}
                        style={{ minHeight: '120px' }}
                      >
                        {branches.map((branch) => (
                          <option
                            key={branch.id}
                            value={branch.id}
                          >
                            {branch.name} ({branch.code})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Giữ Ctrl (Windows) hoặc Cmd (Mac) để chọn nhiều. Không chọn dòng nào = áp dụng mọi chi nhánh.
                      </Form.Text>
                    </Form.Group>
                  )}
                  <Form.Check
                    type="switch"
                    id="edit-menu-contributor"
                    className="mb-3"
                    label="Quản lý món (tạo/sửa/duyệt món trong menu)"
                    checked={editForm.menu_contributor}
                    onChange={(e) =>
                      setEditForm((s) => ({ ...s, menu_contributor: e.target.checked }))
                    }
                  />
                  {editForm.menu_contributor && (
                    <Form.Group className="form-group">
                      <Form.Label>Giới hạn chi nhánh QL món (để trống = tất cả)</Form.Label>
                      <Form.Select
                        multiple
                        value={editForm.menu_contributor_branch_ids}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                          setEditForm((s) => ({ ...s, menu_contributor_branch_ids: selected }));
                        }}
                        style={{ minHeight: '120px' }}
                      >
                        {branches.map((branch) => (
                          <option
                            key={branch.id}
                            value={branch.id}
                          >
                            {branch.name} ({branch.code})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Giữ Ctrl (Windows) hoặc Cmd (Mac) để chọn nhiều. Không chọn dòng nào = áp dụng mọi chi nhánh.
                      </Form.Text>
                    </Form.Group>
                  )}
                  <Form.Check
                    type="switch"
                    id="edit-trainer"
                    className="mb-3"
                    label="Trainer (ca của NV dùng làm ca training cho nhân viên mới, toàn hệ thống)"
                    checked={editForm.trainer}
                    onChange={(e) =>
                      setEditForm((s) => ({ ...s, trainer: e.target.checked }))
                    }
                  />
                  <hr className="my-2" />
                  <Form.Check
                    type="switch"
                    id="edit-flagged"
                    className="mb-3"
                    label="Gắn cờ tài khoản (NV bị giám sát chặt hơn, hiện cảnh báo)"
                    checked={editForm.flagged}
                    onChange={(e) =>
                      setEditForm((s) => ({ ...s, flagged: e.target.checked }))
                    }
                  />
                  {editForm.flagged && (
                    <Form.Group className="form-group mb-3">
                      <Form.Label>Lý do gắn cờ (hiện cho chính NV thấy)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={editForm.flag_reason}
                        onChange={(e) =>
                          setEditForm((s) => ({ ...s, flag_reason: e.target.value }))
                        }
                      />
                    </Form.Group>
                  )}
                </div>
              )}
            </div>
          </Form>
          <Modal.Footer className="mt-4">
            <ButtonComponent
              type="button"
              className="btn btn-warning"
              onClick={handleResetRevenueRewardRate}
            >
              Reset điểm
            </ButtonComponent>
            <ButtonComponent
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditModal(false)}
            >
              {t('close_popup')}
            </ButtonComponent>
            <ButtonComponent
              type="button"
              className="btn btn-success"
              onClick={handleSaveEdit}
            >
              {t('save_changes')}
            </ButtonComponent>
          </Modal.Footer>
        </div>
      </Modal>

      {/* #024038 — Từ chối hồ sơ ứng tuyển: BẮT BUỘC ghi lý do, lý do được gửi cho ứng viên
          qua Zalo. (Luật "cấm nhập lý do" chỉ áp cho luồng kiểm NVL/check-in, không áp ở đây.) */}
      <Modal show={!!rejectingUser} onHide={() => setRejectingUser(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Từ chối hồ sơ ứng tuyển</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Hồ sơ: <strong>{rejectingUser?.full_name || rejectingUser?.username}</strong>
            {rejectingUser?.phone_number ? ` — ${rejectingUser.phone_number}` : ''}
          </p>
          <Form.Group>
            <Form.Label>Lý do từ chối (gửi cho ứng viên qua Zalo)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ví dụ: Chưa đủ tuổi lao động theo quy định chi nhánh"
            />
          </Form.Group>
          <p className="small text-muted mt-2 mb-0">
            Ứng viên vẫn nộp lại được hồ sơ mới bằng số điện thoại này.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <ButtonComponent
            className="btn btn-secondary"
            onClick={() => setRejectingUser(null)}
          >
            Huỷ
          </ButtonComponent>
          <ButtonComponent
            className="btn btn-danger"
            onClick={handleConfirmReject}
            disabled={rejecting}
          >
            {rejecting ? 'Đang từ chối...' : 'Từ chối hồ sơ'}
          </ButtonComponent>
        </Modal.Footer>
      </Modal>

      <CompetitionAnalysisModal
        show={showAnalysisModal}
        onHide={() => {
          setShowAnalysisModal(false);
          setApprovingUser(null);
        }}
        employee={approvingUser}
        branchId={approvingUser?.branch_id || approvingUser?.branch?.id}
        onApprove={handleConfirmApprove}
      />

      {showManualPointModal && selectedUserForPoint && (
        <ManualPointModal
          show={showManualPointModal}
          onHide={() => {
            setShowManualPointModal(false);
            setSelectedUserForPoint(null);
          }}
          employeeId={selectedUserForPoint.id}
          employeeName={selectedUserForPoint.full_name || selectedUserForPoint.username}
          currentMonth={format(new Date(), 'yyyy-MM')}
          payrollStatus={null}
          onSuccess={() => {
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </div>
  );
}
